import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Fade,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { 
  Category as CategoryIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from "@mui/icons-material";
import Category from "../../models/finance/Category";
import { CategoryType } from "../../models/finance/CategoryType";
import { validateCategoryName } from "../../utils/categoryValidation";

interface Props {
  open: boolean;
  category: Category;
  categories: Category[];
  categoryType: CategoryType;
  onClose: () => void;
  onDelete: (category: Category, fallbackId?: string) => void;
  onUpdate: (
    oldCategory: Category,
    newTitle?: string,
    newIsActive?: boolean
  ) => void;
  getFinanceItemsNumber: (
    categoryType: CategoryType,
    categoryId: string
  ) => Promise<number>;
}

const CategoryEditDialogComponent: React.FC<Props> = ({
  open,
  category,
  categories,
  categoryType,
  onClose,
  onDelete,
  onUpdate,
  getFinanceItemsNumber,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const ARCHIVED_SENTINEL = "__ARCHIVED__";
  const MAX_LENGTH = 30;
  const [editName, setEditName] = useState(category.title);
  const [editActive, setEditActive] = useState(category.isActive);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fallbackCategoryId, setFallbackCategoryId] = useState<string>("");
  const [itemCount, setItemCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    const trimmedName = editName.trim();
    const error = validateCategoryName(trimmedName, MAX_LENGTH);
    if (error) return;

    const newName = trimmedName !== category.title ? trimmedName : undefined;
    const newIsActive = editActive !== category.isActive ? editActive : undefined;

    setLoading(true);
    try {
      onUpdate(category, newName, newIsActive);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    const fallback =
      fallbackCategoryId && fallbackCategoryId !== ARCHIVED_SENTINEL
        ? fallbackCategoryId
        : undefined;
    setLoading(true);
    try {
      onDelete(category, fallback);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (loading) {
      return;
    }
    setDeleteDialogOpen(false);
  };

  useEffect(() => {
    if (deleteDialogOpen && itemCount === null) {
      getFinanceItemsNumber(categoryType, category.id)
        .then((count) => setItemCount(count))
        .catch(() => setItemCount(0));
    }
  }, [deleteDialogOpen, categoryType, category.id, getFinanceItemsNumber, itemCount]);

  // Reset form when category changes
  useEffect(() => {
    setEditName(category.title);
    setEditActive(category.isActive);
    setDeleteDialogOpen(false);
    setFallbackCategoryId("");
    setItemCount(null);
  }, [category]);

  // Default the replacement selection to Archived when entering delete confirmation
  useEffect(() => {
    if (deleteDialogOpen) {
      setFallbackCategoryId(ARCHIVED_SENTINEL);
    }
  }, [deleteDialogOpen]);

  const fallbackOptions = categories.filter((cat) => cat.id !== category.id && cat.isActive);
  const destinationOptions = [
    {
      id: ARCHIVED_SENTINEL,
      label: "Archived (default)",
      description: "Moves linked transactions to Archived.",
    },
    ...fallbackOptions.map((cat) => ({
      id: cat.id,
      label: cat.title,
      description: "Move linked transactions to this active category.",
    })),
  ];
  const selectedDestination = destinationOptions.find((option) => option.id === fallbackCategoryId) || null;
  const isExpense = categoryType === CategoryType.Expense;
  const themeColor = isExpense ? theme.palette.error : theme.palette.success;
  const modalAccent = theme.palette.info;
  const hasChanges = editName.trim() !== category.title || editActive !== category.isActive;
  const nameError = validateCategoryName(editName.trim(), MAX_LENGTH);
  const isNameValid = !nameError;
  const canConfirmDelete = !loading && !(itemCount !== null && itemCount > 0 && !fallbackCategoryId);

  return (
    <>
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={open}
        onClose={onClose}
        sx={{ zIndex: (zTheme) => zTheme.zIndex.drawer + 4 }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 540 },
            maxWidth: "100%",
            height: { xs: "92dvh", sm: "100%" },
            background: theme.palette.background.default,
            borderLeft: { sm: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` },
            borderTop: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`, sm: "none" },
            borderTopLeftRadius: { xs: 18, sm: 0 },
            borderTopRightRadius: { xs: 18, sm: 0 },
            overflow: "hidden",
          },
        }}
      >
      <Fade in={open} timeout={300}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${modalAccent.light}20 0%, ${modalAccent.main}20 100%)`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: `linear-gradient(135deg, ${modalAccent.main}, ${modalAccent.dark})`,
                }}
              >
                <CategoryIcon sx={{ color: "white" }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: modalAccent.main }}>
                  {`Edit ${isExpense ? "Expense" : "Income"} Category`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update name and availability for new transactions
                </Typography>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.08),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.2 }}>
                <Box sx={{ mt: 0.4 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1.1, letterSpacing: "0.02em" }}
                  >
                    Category Type and Availability
                  </Typography>
                  <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      label={isExpense ? "Expense" : "Income"}
                      variant="outlined"
                      sx={{ borderColor: alpha(theme.palette.text.primary, 0.18) }}
                    />
                    <Chip
                      size="small"
                      label={editActive ? "Active" : "Inactive"}
                      sx={{
                        bgcolor: alpha(themeColor.main, 0.12),
                        color: themeColor.light,
                        border: `1px solid ${alpha(themeColor.main, 0.24)}`,
                      }}
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Category Details
                  </Typography>
                  <TextField
                    autoFocus
                    label="Category Name"
                    value={editName}
                    onChange={(e) => {
                      if (e.target.value.length <= MAX_LENGTH) {
                        setEditName(e.target.value);
                      }
                    }}
                    error={Boolean(nameError)}
                    fullWidth
                    variant="outlined"
                    helperText={nameError || `${editName.length}/${MAX_LENGTH} characters`}
                    inputProps={{ maxLength: MAX_LENGTH }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeColor.main,
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeColor.main,
                            borderWidth: 2,
                          },
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        color: editName.length > MAX_LENGTH * 0.8
                          ? theme.palette.warning.main
                          : theme.palette.text.secondary,
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Category Status
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(themeColor.main, 0.04),
                    border: `1px solid ${alpha(themeColor.main, 0.12)}`,
                  }}>
                    <Switch
                      checked={editActive}
                      onChange={(e) => setEditActive(e.target.checked)}
                      sx={{
                        mt: 0.5,
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: themeColor.main,
                          '&:hover': {
                            backgroundColor: alpha(themeColor.main, 0.08),
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: themeColor.main,
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: themeColor.main }}>
                        {editActive ? "Active Category" : "Inactive Category"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {editActive 
                          ? "This category is active and can be used for new transactions"
                          : "This category is inactive and hidden from transaction selection"
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {hasChanges && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    You have unsaved changes. Click "Save Changes" to apply them.
                  </Alert>
                )}
            </Box>
          </Box>

          <Divider />

          <Box
            sx={{
              p: 3,
              mt: "auto",
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={1.2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Button
                onClick={handleOpenDeleteDialog}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  width: { xs: "100%", sm: "auto" },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                Delete
              </Button>

              <Stack direction="row" spacing={1.2} sx={{ width: { xs: "100%", sm: "auto" } }}>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    flex: { xs: 1, sm: "unset" },
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.text.primary,
                      backgroundColor: alpha(theme.palette.text.primary, 0.04),
                    },
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  onClick={handleSave}
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!isNameValid || !hasChanges || loading}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    flex: { xs: 1, sm: "unset" },
                    background: hasChanges 
                      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                      : theme.palette.action.disabledBackground,
                    '&:hover': {
                      background: hasChanges 
                        ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                        : theme.palette.action.disabledBackground,
                      transform: hasChanges ? 'translateY(-1px)' : 'none',
                    },
                    '&:disabled': {
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  Save Changes
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Fade>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: 2.5, sm: 3 },
            border: `1px solid ${alpha(theme.palette.error.main, 0.28)}`,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            p: { xs: 2.2, sm: 2.5 },
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.error.main, 0.2), color: theme.palette.error.light }}>
              <WarningAmberRoundedIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.error.light }}>
                Delete Category
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Review impact before permanently removing this category.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2.25, sm: 3 }, pt: { xs: 3, sm: 3.6 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            <Alert severity="warning" sx={{ borderRadius: 2, py: 0.55 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                You are about to delete "{category.title}". This action cannot be undone.
              </Typography>
            </Alert>

            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
              <Chip size="small" label={isExpense ? "Expense" : "Income"} />
              <Chip size="small" label={`Category: ${category.title}`} />
              {itemCount !== null && itemCount > 0 && (
                <Chip size="small" label={`Fallback options: ${fallbackOptions.length + 1}`} />
              )}
            </Stack>

            {itemCount === null && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: theme.palette.text.secondary }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Checking linked transactions...</Typography>
              </Box>
            )}

            {itemCount !== null && itemCount > 0 && (
              <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.8 }}>
                  Reassign linked transactions
                </Typography>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                  {itemCount} transaction{itemCount !== 1 ? "s are" : " is"} linked to this category.
                  Choose where to move {itemCount !== 1 ? "them" : "it"}.
                </Typography>

                <Autocomplete
                  sx={{ mt: 2 }}
                  options={destinationOptions}
                  value={selectedDestination}
                  onChange={(_, option) => setFallbackCategoryId(option?.id || "")}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Destination"
                      placeholder="Find a category"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "action.active", fontSize: 18 }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {fallbackOptions.length === 0 && (
                  <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    No other active categories are available. Linked transactions will be moved to Archived.
                  </Alert>
                )}
              </Box>
            )}

            {itemCount === 0 && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                No linked transactions found. This category can be deleted immediately.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2.25, sm: 2.5 },
            gap: 1.2,
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            disabled={loading}
            sx={{ borderRadius: 2, fontWeight: 500, width: { xs: "100%", sm: "auto" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={!canConfirmDelete}
            sx={{ borderRadius: 2, fontWeight: 600, width: { xs: "100%", sm: "auto" } }}
          >
            {loading ? "Deleting..." : "Delete Category"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoryEditDialogComponent;
